import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useUserStore } from '@/store/user'
import { usePrefetchStore, type PrefetchPhase } from '@/store/usePrefetchStore'
import { getPushPromptPolicy, usePushPromptStore } from '@/store/usePushPromptStore'
import { subscribePushDeviceMarker } from '@/lib/push/pushDeviceMarker'
import { isAnyDialogOpen, useIsAnyDialogOpen } from '@/lib/hooks/useIsAnyDialogOpen'
import { isPushRegisteredOnThisDevice } from '@/lib/push/registerPush'
import {
  getPushPermission,
  probeWebPushSupport,
  type PushPermission,
} from '@/lib/push/pushPermission'
import {
  decidePushPrompt,
  PUSH_PROMPT_SHOW_DELAY_MS,
  type PushPromptSkipReason,
} from '@/lib/push/pushPromptDecision'

export const NOTIFICATION_SETTINGS_PATH = '/settings/notifications'

/**
 * The phases that mean a prefetch is actually RUNNING.
 *
 * Enumerated positively on purpose. The tempting `phase !== 'idle' && phase !==
 * 'cancelled'` is wrong: dismissing the progress banner only clears
 * `isVisible` (usePrefetchStore.ts `dismiss`), so `phase` stays `'done'` until
 * something calls `reset()`. Treating `'done'` as active means one completed
 * offline download suppresses the prompt for the rest of the session — silently,
 * because "nothing appeared" looks identical to "not eligible".
 */
const RUNNING_PREFETCH_PHASES: ReadonlySet<PrefetchPhase> = new Set<PrefetchPhase>([
  'fetching-lists',
  'fetching-details',
  'fetching-attachments',
])

/**
 * Skip reasons that must also RETRACT an already-visible card. The rest are
 * "don't reveal yet" only: a transient prefetch starting must not yank a card
 * the user is currently reading.
 *
 * `dialog-open` is one of the rest, and a visible card still does not stay on
 * screen over a dialog: PushPromptCard stops RENDERING while one is open. That
 * split is deliberate — retracting here would clear `isVisible` and make the
 * card wait a full reveal delay again after the dialog closes, whereas a
 * render-only hide brings back the offer the user was already reading.
 */
const RETRACTING_REASONS: ReadonlySet<PushPromptSkipReason> = new Set<PushPromptSkipReason>([
  'no-user',
  'offline',
  'already-registered',
  'permission-denied',
  'unsupported',
  'dismissed-forever',
  'on-settings-route',
])

/**
 * Drives soft-prompt visibility. Mounted once, near the root.
 *
 * Reveal is delayed and then RE-VALIDATED: between scheduling and firing, the
 * user may have enabled push from the settings screen, gone offline or logged
 * out, and the underlying registration marker is localStorage (not reactive),
 * so the pre-show re-read is what keeps a stale timer from showing a card that
 * no longer applies. The OS permission is the one input that cannot join that
 * synchronous re-check — reading it is async — so it is refreshed on wake
 * instead; see the permission effect below.
 *
 * `isOnline` is passed in rather than read here — RootLayout already subscribes,
 * and a second `useNativeOnlineStatus()` would duplicate the native listener.
 */
export function usePushPromptGate({ isOnline }: { isOnline: boolean }): void {
  const userId = useUserStore(s => s.user?.id ?? null)
  const prefetchPhase = usePrefetchStore(s => s.phase)
  const byUser = usePushPromptStore(s => s.byUser)
  const isVisible = usePushPromptStore(s => s.isVisible)
  const isSessionSuppressed = usePushPromptStore(s => s.isSessionSuppressed)
  const show = usePushPromptStore(s => s.show)
  const hide = usePushPromptStore(s => s.hide)
  const resetSessionSuppression = usePushPromptStore(s => s.resetSessionSuppression)
  const { pathname } = useLocation()
  // Re-evaluation TRIGGER only. `evaluate` re-reads the DOM itself, because this
  // value reaches the effect below only after React re-renders, and a reveal
  // timer already due can fire in that window over a dialog that has just opened
  // (see useIsAnyDialogOpen). This value is what makes a closing dialog
  // schedule the reveal again; without it in the deps below, a prompt deferred
  // by a dialog would wait for some unrelated change — a focus, a navigation —
  // to be offered at all. The reveal delay restarts on close, so the card
  // arrives a full delay after the user is done with the dialog, not in the
  // same instant it disappears.
  const isDialogOpen = useIsAnyDialogOpen()

  const [permission, setPermission] = useState<PushPermission | null>(null)
  // Bumped on focus/visibility so state that changed while the app sat
  // untouched is noticed: an expired cooldown (elapsed time is the one input
  // nothing makes reactive) and the OS permission itself, which lives outside
  // the app entirely.
  const [wakeTick, setWakeTick] = useState(0)

  // Read permission once a user is loaded, re-read on account change, and re-read
  // on every wake. The wake re-read is load-bearing, not hygiene: the OS grant is
  // owned by system settings, so the user can revoke it while backgrounded and
  // nothing notifies us. A stale non-denied value lets the card offer an Enable
  // that CANNOT succeed — the real 'denied' would then surface only after the tap,
  // as an error toast plus session suppression. It also works the other way: a
  // permission reset outside the app becomes askable again without a reload.
  //
  // Cheap enough to run per wake: on web this is a synchronous property read
  // behind the memoised capability probe, and on native a pure plugin query that
  // cannot surface a dialog. probeWebPushSupport() is idempotent (`??=`), so the
  // repeat calls reuse the primed promise rather than reopening IndexedDB — it is
  // primed here, long before any click, so registerPush() can call
  // Notification.requestPermission() as its first act after the tap.
  useEffect(() => {
    if (userId === null) {
      setPermission(null)
      return
    }

    let cancelled = false
    void probeWebPushSupport()
    void getPushPermission()
      .then(next => {
        if (!cancelled) setPermission(next)
      })
      .catch(() => {
        if (!cancelled) setPermission('unsupported')
      })

    return () => {
      cancelled = true
    }
  }, [userId, wakeTick])

  // Account switch must inherit nothing from the previous user: not the visible
  // card, and not the session suppression — that flag records THAT user's failed
  // attempt, and leaving it set would silently mute the prompt for the next
  // person to log in without a reload.
  useEffect(() => {
    hide()
    resetSessionSuppression()
  }, [userId, hide, resetSessionSuppression])

  useEffect(() => {
    const onWake = () => setWakeTick(tick => tick + 1)
    window.addEventListener('focus', onWake)
    document.addEventListener('visibilitychange', onWake)
    // The registration marker is read non-reactively below, so a background change to
    // it (a rotation failure retiring this device's registration) would otherwise go
    // unnoticed until some unrelated dependency happened to change.
    const unsubscribeMarker = subscribePushDeviceMarker(onWake)
    return () => {
      window.removeEventListener('focus', onWake)
      document.removeEventListener('visibilitychange', onWake)
      unsubscribeMarker()
    }
  }, [])

  useEffect(() => {
    if (permission === null) return

    const evaluate = () =>
      decidePushPrompt({
        hasUser: userId !== null,
        isOnline,
        // Read at evaluation time, not render time: localStorage is not reactive.
        isRegisteredOnThisDevice: isPushRegisteredOnThisDevice(userId),
        permission,
        policy: getPushPromptPolicy(byUser, userId),
        isSessionSuppressed,
        isPrefetchActive: RUNNING_PREFETCH_PHASES.has(prefetchPhase),
        isOnNotificationSettingsRoute: pathname.startsWith(NOTIFICATION_SETTINGS_PATH),
        // Read at evaluation time, like the registration marker above.
        isAnotherDialogOpen: isAnyDialogOpen(),
        now: Date.now(),
      })

    const decision = evaluate()
    if (!decision.show) {
      if (isVisible && RETRACTING_REASONS.has(decision.reason)) hide()
      return
    }
    if (isVisible) return

    const timer = window.setTimeout(() => {
      // Re-validate: the delay is long enough for any input to have changed.
      if (evaluate().show) show()
    }, PUSH_PROMPT_SHOW_DELAY_MS)

    return () => window.clearTimeout(timer)
  }, [
    permission,
    userId,
    isOnline,
    prefetchPhase,
    byUser,
    pathname,
    isDialogOpen,
    isVisible,
    isSessionSuppressed,
    wakeTick,
    show,
    hide,
  ])
}
